import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getChannelMeterFinancials,
  captureYtAdPerformance,
  getYtAdPerformanceStatus,
} from '../../services/adminApi';
import { formatTimeAgo } from '../../utils/dateFormatters';
import IncomeDeckModal from './IncomeDeckModal';
import BackfillEarningsHistoryModal from './BackfillEarningsHistoryModal';
import { Button, EmptyState, Spinner, StatusPill, tableStyles } from '../ui';

// ---------------------------------------------------------------------------
// Types (mirror backend CMReportPeriod shape)
// ---------------------------------------------------------------------------

interface CMRevenueSourceEntry {
  revenueSource: string;
  grossUsd: number;
  earningsUsd: number;
  performance: number;
  performanceMetric: string;
  /** DS rows: earnings-based (tracker convention); Partner Ad rows: gross-based; subs: null. */
  ecpm?: number | null;
  ecpmEarnings?: number | null;
  /** Gross per 1,000 views for views-metric rows (Partner Ad, Shorts) — a per-view rate. */
  rpm?: number | null;
}

interface CMChannelBreakdown {
  channelName: string;
  channelId: string;
  channelUrl: string;
  grossUsd: number;
  earningsUsd: number;
  performance: number;
  performanceMetric: string;
  /** DS channels: earnings ÷ impressions (tracker convention); AdSense: ad-only gross ÷ VIEWS (per-view rate). */
  ecpm?: number | null;
  ecpmEarnings?: number | null;
  /** Ad-only gross (Partner Ad rows) backing the rate. */
  adGrossUsd?: number;
  /** All ad-type gross incl. Shorts Partner Ad — the reconciliation subtotal. */
  adRevenueUsd?: number;
  shortsGrossUsd?: number;
  shortsViews?: number;
  shortsRpm?: number | null;
  /** True auction (Google-sold) ad impressions from YouTube Analytics. */
  ytAdImpressions?: number;
  ytReservedImpressions?: number;
  /** CM ad gross ÷ YT auction impressions × 1000 — the true AdSense eCPM. */
  ytEcpm?: number | null;
  revenueSources: CMRevenueSourceEntry[];
}

/** Response-only summary of the YouTube ad-performance join for this month. */
interface YtAdPerformanceSummary {
  month: string;
  status: 'complete' | 'running' | 'failed' | 'missing' | 'error';
  fetchedAt?: string;
  progress?: { current: number; total: number };
  error?: string;
  channelsMatched: number;
  channelsTotal: number;
  grossCoveragePct?: number | null;
  ytAuctionGrossUsd?: number;
  cmPartnerAdGrossUsd?: number;
  grossDeltaPct?: number | null;
  ytReservedImpressions?: number;
  cmDirectSoldPerformance?: number;
}

interface CMUserFinancials {
  userId?: string;
  userName: string;
  userEmail?: string;
  userTags: string[];
  dataSource?: 'cm' | 'sheet';
  directSold: {
    grossUsd: number;
    earningsUsd: number;
    channels: CMChannelBreakdown[];
  };
  adSense: {
    grossUsd: number;
    earningsUsd: number;
    channels: CMChannelBreakdown[];
  };
  totalGrossUsd: number;
  totalEarningsUsd: number;
}

interface CMReportPeriod {
  reportDate: string;
  directSoldReportId: string;
  adSenseReportId: string;
  users: CMUserFinancials[];
  dataSource?: 'cm' | 'sheet' | 'mixed';
  totals: {
    directSoldGross: number;
    directSoldEarnings: number;
    directSoldPerformance?: number;
    /** Earnings-based (Channel Earnings ÷ impressions × 1000) — matches the Monthly Tracker sheets. */
    directSoldEcpm?: number | null;
    directSoldEcpmEarnings?: number | null;
    adSenseGross: number;
    adSenseEarnings: number;
    /** Partner Ad performance — monetized VIEWS (not ad impressions). */
    adSensePerformance?: number;
    /** Ad-only gross ÷ views × 1000 — a per-view rate (legacy headline). */
    adSenseEcpm?: number | null;
    adSenseAdRevenueUsd?: number;
    adSenseShortsGross?: number;
    adSenseShortsViews?: number;
    adSenseShortsRpm?: number | null;
    /** True auction ad impressions from YouTube Analytics (matched channels). */
    adSenseAdImpressions?: number;
    /** CM ad gross ÷ YT auction impressions × 1000 — the true AdSense eCPM. */
    adSenseEcpmTrue?: number | null;
    totalGross: number;
    totalEarnings: number;
  };
  /** Response-only: the Earnings Corrections applied to this month (corrected basis). */
  corrections?: PeriodCorrections;
}

interface CorrectionRowValues {
  grossUsd: number;
  earningsUsd: number;
  performance: number;
}

/** Mirrors the backend PeriodCorrectionsSummary. */
interface PeriodCorrections {
  applied: Array<{ id: string; title: string; kind: string; appliedAt: string; clientNote: string | null }>;
  rows: Array<{
    rowKeyId: string;
    correctionId: string;
    bucket: 'directSold' | 'adSense';
    userKey: string;
    userName: string;
    channelId: string;
    channelName: string;
    revenueSource: string;
    paid: CorrectionRowValues;
    corrected: CorrectionRowValues;
    /** ChannelMeter's value moved after the correction was written. */
    drift: boolean;
  }>;
  unresolved: Array<{ correctionId: string; rowKeyId: string; reason: string }>;
  paidTotals: CMReportPeriod['totals'];
  /** Corrections could not be loaded — these are ChannelMeter's figures. */
  unavailable?: boolean;
}

/** Stable identity for a user row — falls back to payee name when the row has no CM userId. */
const userRowKey = (u: CMUserFinancials): string =>
  u.userId && u.userId !== '' ? u.userId : `payee:${u.userName}`;

/** Paid → corrected earnings for a user or channel an applied correction touched. */
interface CorrectedMark {
  paidEarningsUsd: number;
  correctedEarningsUsd: number;
  drift: boolean;
}

const channelMarkKey = (bucket: 'directSold' | 'adSense', userKey: string, channelId: string): string =>
  `${bucket}|${userKey}|${channelId}`;

/** Corrected rows summed per user (userRowKey) and per bucket × user × channel. */
function correctionMarks(corrections: PeriodCorrections | undefined): {
  users: Map<string, CorrectedMark>;
  channels: Map<string, CorrectedMark>;
} {
  const users = new Map<string, CorrectedMark>();
  const channels = new Map<string, CorrectedMark>();
  const add = (map: Map<string, CorrectedMark>, key: string, row: PeriodCorrections['rows'][number]) => {
    const mark = map.get(key) ?? { paidEarningsUsd: 0, correctedEarningsUsd: 0, drift: false };
    mark.paidEarningsUsd += row.paid.earningsUsd;
    mark.correctedEarningsUsd += row.corrected.earningsUsd;
    mark.drift = mark.drift || row.drift;
    map.set(key, mark);
  };
  for (const row of corrections?.rows ?? []) {
    add(users, row.userKey, row);
    add(channels, channelMarkKey(row.bucket, row.userKey, row.channelId), row);
  }
  return { users, channels };
}

const markTitle = (mark: CorrectedMark): string =>
  `ChannelMeter paid ${formatCurrency(mark.paidEarningsUsd)} → corrected ${formatCurrency(mark.correctedEarningsUsd)} (Earnings Correction)` +
  (mark.drift ? '. ChannelMeter changed this row after the correction was written; review the correction.' : '');

const CorrectedPill: React.FC<{ mark: CorrectedMark }> = ({ mark }) => (
  <span title={markTitle(mark)} style={{ marginLeft: 8 }}>
    <StatusPill variant={mark.drift ? 'pending' : 'featured'} noDot>
      Corrected
    </StatusPill>
  </span>
);

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null || isNaN(amount)) return '-';
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
};

/** "+$1.00" / "-$1.00" — a correction's change vs ChannelMeter. */
const formatSignedUsd = (amount: number): string => {
  const rounded = Math.round(amount * 100) / 100;
  return rounded > 0 ? `+${formatCurrency(rounded)}` : formatCurrency(rounded === 0 ? 0 : rounded);
};

const formatPerformance = (value: number, metric: string): string => {
  if (!value) return '-';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M ${metric}`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K ${metric}`;
  return `${Math.round(value)} ${metric}`;
};

const formatEcpm = (ecpm?: number | null): string => {
  if (ecpm === undefined || ecpm === null || isNaN(ecpm)) return '-';
  return `$${ecpm.toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// Channel table sub-component (shared between Direct Sold & AdSense)
// ---------------------------------------------------------------------------

interface ChannelTableProps {
  label: string;
  prefix: string;
  userId: string;
  channels: CMChannelBreakdown[];
  expandedChannelKey: string | null;
  onToggleChannel: (key: string) => void;
  /** Channels an applied Earnings Correction touched (channelMarkKey). */
  correctedChannels?: Map<string, CorrectedMark>;
}

const ChannelTable: React.FC<ChannelTableProps> = ({
  label, prefix, userId, channels, expandedChannelKey, onToggleChannel, correctedChannels,
}) => (
  <div style={styles.expandedSection}>
    <h4 style={styles.expandedHeading}>
      {label} ({channels.length})
    </h4>
    <div style={tableStyles.wrapScroll}>
    <table style={tableStyles.table}>
      <thead>
        <tr>
          <th style={{ ...tableStyles.th, width: 24 }}></th>
          <th style={tableStyles.th}>Channel</th>
          <th style={tableStyles.th}>Channel ID</th>
          <th style={tableStyles.thNum} title="Total gross — includes subscription/premium revenue">Gross</th>
          <th style={tableStyles.thNum}>Earnings</th>
          {prefix === 'as' ? (
            <>
              <th
                style={tableStyles.thNum}
                title="ChannelMeter Partner Ad performance — monetized long-form VIEWS, not ad impressions."
              >Views (CM) ⓘ</th>
              <th
                style={tableStyles.thNum}
                title="Shorts Partner Ad — pool-allocated ad revenue. RPM = gross ÷ views × 1000. Never blended into eCPM (Shorts has no impressions)."
              >Shorts ⓘ</th>
              <th
                style={tableStyles.thNum}
                title="True auction (Google-sold) ad impressions from YouTube Analytics (content-owner API)."
              >Ad Impr. (YT) ⓘ</th>
              <th
                style={tableStyles.thNum}
                title="CM Partner Ad gross ÷ YT auction ad impressions × 1000 — long-form ads only. '—' until YT impressions are captured for this month."
              >eCPM (true) ⓘ</th>
            </>
          ) : (
            <>
              <th style={tableStyles.thNum}>Performance</th>
              <th
                style={tableStyles.thNum}
                title="Channel earnings ÷ impressions × 1000 — matches the Monthly Tracker sheets (CM gross is 2× earnings, so this is half a gross-based rate)."
              >eCPM ⓘ</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {channels.map((ch) => {
          const chKey = `${prefix}:${userId}:${ch.channelId}:${ch.channelName}`;
          const isChExpanded = expandedChannelKey === chKey;
          const hasMultipleSources = ch.revenueSources.length > 1;
          return (
            <React.Fragment key={chKey}>
              <tr
                style={{ cursor: hasMultipleSources ? 'pointer' : 'default' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!hasMultipleSources) return;
                  onToggleChannel(isChExpanded ? '' : chKey);
                }}
              >
                <td style={tableStyles.td}>
                  {hasMultipleSources && (
                    <span style={{ fontSize: '9px', color: 'var(--fg-3)' }}>
                      {isChExpanded ? '\u25BC' : '\u25B6'}
                    </span>
                  )}
                </td>
                <td style={tableStyles.td}>
                  {ch.channelName || '-'}
                  {(() => {
                    const mark = correctedChannels?.get(
                      channelMarkKey(prefix === 'as' ? 'adSense' : 'directSold', userId, ch.channelId)
                    );
                    return mark ? <CorrectedPill mark={mark} /> : null;
                  })()}
                </td>
                <td style={tableStyles.td}>
                  <code style={styles.code}>{ch.channelId}</code>
                </td>
                <td style={tableStyles.tdNum}>
                  {formatCurrency(ch.grossUsd)}
                </td>
                <td style={{ ...tableStyles.tdNum, fontWeight: 600 }}>
                  {formatCurrency(ch.earningsUsd)}
                </td>
                {prefix === 'as' ? (
                  <>
                    <td style={tableStyles.tdNum}>
                      {formatPerformance(ch.performance, ch.performanceMetric)}
                    </td>
                    <td style={tableStyles.tdNum}>
                      {ch.shortsViews
                        ? `${formatPerformance(ch.shortsViews, 'views')} · ${formatEcpm(ch.shortsRpm)} RPM`
                        : '—'}
                    </td>
                    <td style={tableStyles.tdNum}>
                      {ch.ytAdImpressions != null
                        ? formatPerformance(ch.ytAdImpressions, 'impr')
                        : '—'}
                    </td>
                    <td style={{ ...tableStyles.tdNum, fontWeight: 600 }}>
                      {ch.ytEcpm != null ? formatEcpm(ch.ytEcpm) : '—'}
                    </td>
                  </>
                ) : (
                  <>
                    <td style={tableStyles.tdNum}>
                      {formatPerformance(ch.performance, ch.performanceMetric)}
                    </td>
                    <td style={tableStyles.tdNum}>
                      {formatEcpm(ch.ecpm)}
                    </td>
                  </>
                )}
              </tr>
              {isChExpanded && ch.revenueSources.map((rs, ri) => (
                <tr key={ri} style={{ background: 'var(--bg-surface-2)' }}>
                  <td style={tableStyles.td}></td>
                  <td style={{ ...tableStyles.td, paddingLeft: '24px', fontStyle: 'italic' }} colSpan={2}>
                    {rs.revenueSource}
                  </td>
                  <td style={tableStyles.tdNum}>
                    {formatCurrency(rs.grossUsd)}
                  </td>
                  <td style={tableStyles.tdNum}>
                    {formatCurrency(rs.earningsUsd)}
                  </td>
                  <td style={tableStyles.tdNum}>
                    {formatPerformance(rs.performance, rs.performanceMetric)}
                  </td>
                  {prefix === 'as' ? (
                    <>
                      <td style={tableStyles.td}></td>
                      <td style={tableStyles.td}></td>
                      <td
                        style={tableStyles.tdNum}
                        title={rs.rpm != null
                          ? 'RPM — gross ÷ views × 1000 (a per-view rate; not an impression eCPM)'
                          : undefined}
                      >
                        {rs.rpm != null ? `${formatEcpm(rs.rpm)} RPM` : formatEcpm(rs.ecpm)}
                      </td>
                    </>
                  ) : (
                    <td style={tableStyles.tdNum}>
                      {formatEcpm(rs.ecpm)}
                    </td>
                  )}
                </tr>
              ))}
              {isChExpanded && prefix === 'as' && ch.adRevenueUsd != null && (
                <tr style={{ background: 'var(--bg-surface-2)' }}>
                  <td style={tableStyles.td}></td>
                  <td
                    style={{ ...tableStyles.td, paddingLeft: '24px', fontWeight: 600 }}
                    colSpan={2}
                    title="Partner Ad + Shorts Partner Ad gross — the portion of Gross that came from ads. The remainder is subscriptions/premium/paid features."
                  >
                    Ad revenue (Partner + Shorts)
                  </td>
                  <td style={{ ...tableStyles.tdNum, fontWeight: 600 }}>
                    {formatCurrency(ch.adRevenueUsd)}
                  </td>
                  <td style={tableStyles.td} colSpan={4}></td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface FinancialsTabProps {
  /** Open Financials → Corrections filtered to a month (YYYY-MM). */
  onViewCorrections?: (month: string) => void;
}

const FinancialsTab: React.FC<FinancialsTabProps> = ({ onViewCorrections }) => {
  const [period, setPeriod] = useState<CMReportPeriod | null>(null);
  const [availablePeriods, setAvailablePeriods] = useState<{ reportDate: string; label: string }[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [expandedChannelKey, setExpandedChannelKey] = useState<string | null>(null);
  // "Show ChannelMeter (paid) values": a ref so every reload (polling, backfill,
  // refresh) keeps the basis without re-running the initial-load effect.
  const [showPaid, setShowPaid] = useState(false);
  const basisRef = useRef<'paid' | 'corrected'>('corrected');

  // YouTube ad-performance capture (true AdSense impressions)
  const [ytSummary, setYtSummary] = useState<YtAdPerformanceSummary | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureProgress, setCaptureProgress] = useState<{ current: number; total: number } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const selectedMonthRef = useRef(0);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const stopYtPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadFinancials = useCallback(async (month: number, refresh?: boolean) => {
    setLoading(true);
    setError(null);
    setExpandedUserId(null);
    setExpandedChannelKey(null);
    try {
      const data = await getChannelMeterFinancials({ month, refresh, basis: basisRef.current });
      setPeriod(data.period);
      setAvailablePeriods(data.availablePeriods || []);
      setLastFetchedAt(data.lastFetchedAt || null);
      setYtSummary(data.ytAdPerformance || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load financials.');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Poll a running capture every 5s; reload financials when it finishes. */
  const startYtPolling = useCallback((month: string) => {
    stopYtPolling();
    setCapturing(true);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getYtAdPerformanceStatus(month);
        const m = status?.month;
        if (m?.status === 'running') {
          setCaptureProgress(m.progress || null);
        } else {
          // complete / failed / doc missing — stop and pick up the joined data.
          stopYtPolling();
          setCapturing(false);
          setCaptureProgress(null);
          loadFinancials(selectedMonthRef.current);
        }
      } catch {
        /* transient poll error — keep polling */
      }
    }, 5000);
  }, [stopYtPolling, loadFinancials]);

  const startYtCapture = useCallback(async () => {
    if (!period) return;
    const month = period.reportDate.substring(0, 7);
    try {
      setCaptureProgress(null);
      await captureYtAdPerformance({ month });
      // 'started' or 'already-running' — either way, watch it.
      startYtPolling(month);
    } catch (err: any) {
      setError(err.message || 'Failed to start YouTube impressions capture.');
    }
  }, [period, startYtPolling]);

  // Resume watching a capture that was already running when the tab loaded.
  useEffect(() => {
    if (ytSummary?.status === 'running' && !pollRef.current) {
      setCaptureProgress(ytSummary.progress || null);
      startYtPolling(ytSummary.month);
    }
  }, [ytSummary, startYtPolling]);

  useEffect(() => stopYtPolling, [stopYtPolling]); // clear the interval on unmount

  useEffect(() => {
    loadFinancials(0);
  }, [loadFinancials]);

  const handleShowPaid = (paid: boolean) => {
    setShowPaid(paid);
    basisRef.current = paid ? 'paid' : 'corrected';
    loadFinancials(selectedMonthRef.current);
  };

  const handleMonthChange = (idx: number) => {
    setSelectedMonth(idx);
    selectedMonthRef.current = idx;
    stopYtPolling();
    setCapturing(false);
    setCaptureProgress(null);
    loadFinancials(idx);
  };

  // -----------------------------------------------------------------------
  // Filtered users (memoized)
  // -----------------------------------------------------------------------

  const corrections = period?.corrections;
  const marks = useMemo(() => correctionMarks(corrections), [corrections]);
  const hasCorrections = !showPaid && (corrections?.applied.length ?? 0) > 0;
  const paidTotals = hasCorrections ? corrections!.paidTotals : null;
  /** "ChannelMeter: $X" under a stat card when a correction moved it. */
  const paidSubLabel = (paid: number | undefined, shown: number | undefined) =>
    paid != null && shown != null && Math.abs(paid - shown) >= 0.005 ? (
      <div style={styles.statSubLabel} title="What ChannelMeter paid, before Earnings Corrections">
        ChannelMeter: {formatCurrency(paid)}
      </div>
    ) : null;

  const filteredUsers = useMemo(() => {
    if (!period) return [];
    if (!searchQuery) return period.users;
    const q = searchQuery.toLowerCase();
    return period.users.filter(
      (u) => u.userName.toLowerCase().includes(q) || (u.userEmail || '').toLowerCase().includes(q)
    );
  }, [period, searchQuery]);

  // Modal state
  const [incomeDeckOpen, setIncomeDeckOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div>
      {/* Stats row */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{period?.users.length ?? '-'}</div>
          <div style={styles.statLabel}>Total Users</div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--accent)' }}>
          <div style={{ ...styles.statValue, color: 'var(--accent)' }}>
            {period ? formatCurrency(period.totals.directSoldGross) : '-'}
          </div>
          <div style={styles.statLabel}>Direct Sold Gross</div>
          {paidTotals && paidSubLabel(paidTotals.directSoldGross, period?.totals.directSoldGross)}
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--success)' }}>
          <div style={{ ...styles.statValue, color: 'var(--success)' }}>
            {period ? formatCurrency(period.totals.adSenseGross) : '-'}
          </div>
          <div style={styles.statLabel}>AdSense Gross</div>
          {paidTotals && paidSubLabel(paidTotals.adSenseGross, period?.totals.adSenseGross)}
        </div>
        <div
          style={{ ...styles.statCard, borderLeftColor: 'var(--accent)' }}
          title="Channel earnings ÷ impressions × 1000 — matches the Monthly Tracker sheets"
        >
          <div style={{ ...styles.statValue, color: 'var(--accent)' }}>
            {period ? formatEcpm(period.totals.directSoldEcpm) : '-'}
          </div>
          <div style={styles.statLabel}>DS eCPM (earnings)</div>
          {paidTotals && paidTotals.directSoldEcpm != null && period?.totals.directSoldEcpm != null &&
            Math.abs(paidTotals.directSoldEcpm - period.totals.directSoldEcpm) >= 0.005 && (
              <div style={styles.statSubLabel}>ChannelMeter: {formatEcpm(paidTotals.directSoldEcpm)}</div>
            )}
        </div>
        <div
          style={{ ...styles.statCard, borderLeftColor: 'var(--viz-4)' }}
          title={
            'CM Partner Ad gross ÷ YouTube auction ad impressions × 1000 — true long-form AdSense eCPM.' +
            (period?.totals.adSenseEcpm != null
              ? ` CM views-based rate (legacy): ${formatEcpm(period.totals.adSenseEcpm)} (ad gross ÷ monetized views).`
              : '')
          }
        >
          <div style={{ ...styles.statValue, color: 'var(--info)' }}>
            {period
              ? period.totals.adSenseEcpmTrue != null
                ? formatEcpm(period.totals.adSenseEcpmTrue)
                : '—'
              : '-'}
          </div>
          <div style={styles.statLabel}>AdSense eCPM (true)</div>
          <div style={styles.statSubLabel}>
            {period
              ? period.totals.adSenseEcpmTrue != null && ytSummary
                ? `${ytSummary.channelsMatched}/${ytSummary.channelsTotal} ch` +
                  (ytSummary.grossCoveragePct != null
                    ? ` · ${ytSummary.grossCoveragePct.toFixed(0)}% of ad gross`
                    : '')
                : 'No YT data — Capture YT Impressions'
              : ''}
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeftColor: 'var(--warning)' }}>
          <div style={{ ...styles.statValue, color: 'var(--warning)' }}>
            {period ? formatCurrency(period.totals.totalEarnings) : '-'}
          </div>
          <div style={styles.statLabel}>Total Earnings</div>
          {paidTotals && paidSubLabel(paidTotals.totalEarnings, period?.totals.totalEarnings)}
        </div>
      </div>

      {/* YT-vs-CM validation note (drives the go/no-go for migrating the sheets later) */}
      {ytSummary?.status === 'complete' && (
        <div style={styles.ytValidationNote}>
          YouTube impressions captured{ytSummary.fetchedAt ? ` ${formatTimeAgo(ytSummary.fetchedAt)}` : ''}
          {ytSummary.ytAuctionGrossUsd != null && ytSummary.cmPartnerAdGrossUsd != null && ytSummary.ytAuctionGrossUsd > 0 && (
            <span title="YT grossRevenue is advertiser-side (pre YouTube platform split); CM Partner Ad gross is the partner share. A stable ~55–65% ratio month-over-month = healthy.">
              {' · '}CM Partner Ad {formatCurrency(ytSummary.cmPartnerAdGrossUsd)} ={' '}
              {((ytSummary.cmPartnerAdGrossUsd / ytSummary.ytAuctionGrossUsd) * 100).toFixed(1)}% of YT auction gross{' '}
              {formatCurrency(ytSummary.ytAuctionGrossUsd)} (advertiser-side) ⓘ
            </span>
          )}
          {ytSummary.ytReservedImpressions != null && ytSummary.cmDirectSoldPerformance != null && ytSummary.cmDirectSoldPerformance > 0 && (
            <span title="Hard cross-check: YT reserved (partner-sold) ad impressions vs ChannelMeter Direct Sold impressions — these measure the same thing and should be within a few percent.">
              {' · '}reserved impr Δ vs CM DS{' '}
              {(((ytSummary.ytReservedImpressions - ytSummary.cmDirectSoldPerformance) / ytSummary.cmDirectSoldPerformance) * 100).toFixed(1)}% ⓘ
            </span>
          )}
        </div>
      )}
      {ytSummary?.status === 'failed' && (
        <div style={{ ...styles.ytValidationNote, color: 'var(--danger)' }}>
          Last YouTube impressions capture failed{ytSummary.error ? `: ${ytSummary.error}` : ''} — try again.
        </div>
      )}

      {/* Earnings Corrections: which figures this month shows */}
      {period && !showPaid && corrections?.unavailable && (
        <div style={{ ...styles.correctionBanner, ...styles.correctionBannerWarn }} role="alert">
          Earnings Corrections couldn’t be loaded, so these are ChannelMeter’s figures and may be missing applied
          corrections. Refresh to try again.
        </div>
      )}
      {period && hasCorrections && paidTotals && (
        <div style={styles.correctionBanner}>
          <div>
            <strong>
              {corrections!.applied.length} Earnings Correction{corrections!.applied.length === 1 ? '' : 's'} applied to this
              month
            </strong>
            {' · '}Direct Sold {formatSignedUsd(period.totals.directSoldEarnings - paidTotals.directSoldEarnings)}
            {Math.abs(period.totals.adSenseEarnings - paidTotals.adSenseEarnings) >= 0.005 &&
              ` · AdSense ${formatSignedUsd(period.totals.adSenseEarnings - paidTotals.adSenseEarnings)}`}
            {' '}vs ChannelMeter. Figures below are corrected; ChannelMeter, its creator emails and the payout sheets keep
            the paid amounts.
            {corrections!.rows.some((r) => r.drift) && (
              <div style={{ marginTop: 4, color: 'var(--danger)' }}>
                ChannelMeter changed {corrections!.rows.filter((r) => r.drift).length} corrected row(s) after the correction
                was written. Review it.
              </div>
            )}
            {corrections!.unresolved.length > 0 && (
              <div style={{ marginTop: 4, color: 'var(--danger)' }}>
                {corrections!.unresolved.length} correction line(s) no longer match a ChannelMeter row and are not shown.
              </div>
            )}
          </div>
          <div style={styles.controlsWrapper}>
            {onViewCorrections && (
              <Button variant="ghost" size="sm" onClick={() => onViewCorrections(period.reportDate.substring(0, 7))}>
                View corrections
              </Button>
            )}
            <Button variant="subtle" size="sm" onClick={() => handleShowPaid(true)} disabled={loading}>
              Show ChannelMeter (paid) values
            </Button>
          </div>
        </div>
      )}
      {period && showPaid && (
        <div style={{ ...styles.correctionBanner, ...styles.correctionBannerNeutral }}>
          <div>Showing exactly what ChannelMeter paid. Earnings Corrections are hidden.</div>
          <Button variant="subtle" size="sm" onClick={() => handleShowPaid(false)} disabled={loading}>
            Show corrected values
          </Button>
        </div>
      )}

      {/* Main section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Financials</h2>
          <div style={styles.controlsWrapper}>
            {/* Month selector */}
            <select
              value={selectedMonth}
              onChange={(e) => handleMonthChange(Number(e.target.value))}
              style={styles.monthSelect}
              disabled={loading}
            >
              {availablePeriods.length > 0
                ? availablePeriods.map((p, i) => (
                    <option key={p.reportDate} value={i}>
                      {p.label}
                    </option>
                  ))
                : <option value={0}>Loading...</option>}
            </select>

            {/* Search */}
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />

            {lastFetchedAt && (
              <span style={{ fontSize: '11px', color: 'var(--fg-3)' }}>
                Data from {formatTimeAgo(lastFetchedAt)}
              </span>
            )}
            <Button
              variant="subtle"
              size="sm"
              onClick={() => loadFinancials(selectedMonth, true)}
              disabled={loading}
            >
              Refresh
            </Button>

            {capturing || ytSummary?.status === 'running' ? (
              <StatusPill variant="pending">
                Capturing YT…{captureProgress ? ` ${captureProgress.current}/${captureProgress.total}` : ''}
              </StatusPill>
            ) : period ? (
              <Button
                variant="subtle"
                size="sm"
                onClick={startYtCapture}
                disabled={loading}
                title="Fetch true AdSense ad impressions for this month from YouTube Analytics (auction ad types, per channel). Runs in the background (~2–3 min)."
              >
                {ytSummary?.status === 'complete' ? 'Re-capture YT Impressions' : 'Capture YT Impressions'}
              </Button>
            ) : null}

            <Button
              variant="primary"
              size="sm"
              onClick={() => setIncomeDeckOpen(true)}
              disabled={loading}
              title="Generate a Gamma deck or a client-ready PDF report of Direct Sold + AdSense income for selected channels"
            >
              Generate Deck / Report
            </Button>

            <Button
              variant="subtle"
              size="sm"
              onClick={() => setBackfillOpen(true)}
              disabled={loading}
              title="Backfill historical channel earnings from Google Sheets into Firestore"
            >
              Backfill History
            </Button>

          </div>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {loading ? (
          <div style={styles.loadingRow}>
            <Spinner label="Loading financials…" />
          </div>
        ) : !period ? (
          <EmptyState
            brand={false}
            maxWidth={480}
            title="No financials for this period"
            subtitle="ChannelMeter hasn't published a report for this month yet. Pick another month or hit Refresh."
          />
        ) : filteredUsers.length === 0 ? (
          <EmptyState
            brand={false}
            maxWidth={480}
            title={searchQuery ? 'No matching users' : 'No users in this period'}
            subtitle={
              searchQuery
                ? 'No users match that search. Try a different name or email.'
                : 'This report period has no payees. Pick another month from the selector.'
            }
          />
        ) : (
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={{ ...tableStyles.th, width: 24 }}></th>
                  <th style={tableStyles.th}>User Name</th>
                  <th style={tableStyles.th}>Email</th>
                  <th style={tableStyles.thNum}>DS Gross</th>
                  <th style={tableStyles.thNum}>DS Earnings</th>
                  <th style={tableStyles.thNum}>AdSense Gross</th>
                  <th style={tableStyles.thNum}>AdSense Earnings</th>
                  <th style={tableStyles.thNum}>Total Earnings</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const rowKey = userRowKey(user);
                  const isExpanded = expandedUserId === rowKey;
                  const hasChannels =
                    user.directSold.channels.length > 0 || user.adSense.channels.length > 0;
                  const isHistorical = user.dataSource === 'sheet';

                  return (
                    <React.Fragment key={rowKey}>
                      {/* Main user row */}
                      <tr
                        onClick={() => {
                          if (!hasChannels) return;
                          setExpandedUserId(isExpanded ? null : rowKey);
                          setExpandedChannelKey(null);
                        }}
                        onMouseEnter={(ev) => {
                          if (!isExpanded) ev.currentTarget.style.background = tableStyles.rowHoverBackground;
                        }}
                        onMouseLeave={(ev) => {
                          if (!isExpanded) ev.currentTarget.style.background = '';
                        }}
                        style={{
                          cursor: hasChannels ? 'pointer' : 'default',
                          background: isExpanded ? tableStyles.trExpanded.background : undefined,
                        }}
                      >
                        <td style={tableStyles.td}>
                          {hasChannels && (
                            <span style={{ fontSize: '10px', color: 'var(--fg-3)' }}>
                              {isExpanded ? '\u25BC' : '\u25B6'}
                            </span>
                          )}
                        </td>
                        <td style={{ ...tableStyles.td, fontWeight: 500 }}>
                          {user.userName || '-'}
                          {!showPaid && marks.users.get(rowKey) && <CorrectedPill mark={marks.users.get(rowKey)!} />}
                          {isHistorical && (
                            <span title="Backfilled from historical Google Sheet data" style={{ marginLeft: 8 }}>
                              <StatusPill variant="approved" noDot>
                                Historical
                              </StatusPill>
                            </span>
                          )}
                        </td>
                        <td style={tableStyles.td}>{user.userEmail || '\u2014'}</td>
                        <td style={tableStyles.tdNum}>
                          {formatCurrency(user.directSold.grossUsd)}
                        </td>
                        <td style={tableStyles.tdNum}>
                          {formatCurrency(user.directSold.earningsUsd)}
                        </td>
                        <td style={tableStyles.tdNum}>
                          {formatCurrency(user.adSense.grossUsd)}
                        </td>
                        <td style={tableStyles.tdNum}>
                          {formatCurrency(user.adSense.earningsUsd)}
                        </td>
                        <td style={{ ...tableStyles.tdNum, fontWeight: 700 }}>
                          {formatCurrency(user.totalEarningsUsd)}
                        </td>
                      </tr>

                      {/* Expanded: channel breakdown */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} style={styles.expandedTd}>
                            <div style={styles.expandedContent}>
                              {user.directSold.channels.length > 0 && (
                                <ChannelTable
                                  label="Direct Sold Channels"
                                  prefix="ds"
                                  userId={rowKey}
                                  channels={user.directSold.channels}
                                  expandedChannelKey={expandedChannelKey}
                                  onToggleChannel={setExpandedChannelKey}
                                  correctedChannels={showPaid ? undefined : marks.channels}
                                />
                              )}
                              {user.adSense.channels.length > 0 && (
                                <ChannelTable
                                  label="AdSense Channels"
                                  prefix="as"
                                  userId={rowKey}
                                  channels={user.adSense.channels}
                                  expandedChannelKey={expandedChannelKey}
                                  onToggleChannel={setExpandedChannelKey}
                                  correctedChannels={showPaid ? undefined : marks.channels}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {incomeDeckOpen && (
        <IncomeDeckModal onClose={() => setIncomeDeckOpen(false)} />
      )}
      {backfillOpen && (
        <BackfillEarningsHistoryModal
          onClose={() => setBackfillOpen(false)}
          onCompleted={() => {
            // Refresh the period list so newly backfilled months appear in the dropdown
            loadFinancials(selectedMonth, true);
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderLeft: '4px solid var(--accent)',
    borderRadius: 'var(--radius-lg)',
    padding: '16px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: 'var(--fg-1)',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  statLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--fg-4)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },
  statSubLabel: {
    fontSize: '10px',
    color: 'var(--fg-3)',
    marginTop: '2px',
  },
  ytValidationNote: {
    fontSize: '11px',
    color: 'var(--fg-3)',
    margin: '-16px 2px 24px',
  },
  section: {
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    padding: '20px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  controlsWrapper: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  monthSelect: {
    padding: '8px 12px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontSize: '13px',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-sans)',
    cursor: 'pointer',
  },
  searchInput: {
    padding: '8px 12px',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontSize: '13px',
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    width: '240px',
    fontFamily: 'var(--font-sans)',
  },
  expandedTd: {
    padding: '0 12px 16px',
    background: 'var(--bg-surface-2)',
  },
  expandedContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
    padding: '16px',
  },
  expandedSection: {},
  expandedHeading: {
    margin: '0 0 8px 0',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  code: {
    fontSize: '12px',
    fontFamily: 'var(--font-mono)',
    background: 'var(--cg-neutral-50)',
    padding: '2px 6px',
    borderRadius: 'var(--radius-xs)',
  },
  loadingRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 0',
  },
  correctionBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
    fontSize: '13px',
    lineHeight: 1.45,
    color: 'var(--fg-1)',
    background: 'var(--accent-2-soft)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    marginBottom: '16px',
  },
  correctionBannerWarn: {
    background: 'var(--warning-soft)',
  },
  correctionBannerNeutral: {
    background: 'var(--bg-surface-2)',
    color: 'var(--fg-2)',
  },
  errorBanner: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--danger)',
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    marginBottom: '12px',
  },
};

export default FinancialsTab;
